(() => {
  'use strict';

  const GLOBAL_KEY = '__icarsoftCarMultibrand';
  const VERSION = '4';
  const existing = window[GLOBAL_KEY];

  if (existing?.version === VERSION && existing?.initAll) {
    existing.initAll(document);
    return;
  }

  existing?.destroyAll?.(document);

  const instances = new WeakMap();
  const filterAliases = new Map([
    // The supplied static design uses battery-test in the filter and
    // battery-tester on product cards. Treat both spellings as equivalent.
    ['battery-test', ['battery-test', 'battery-tester']],
    ['battery-tester', ['battery-test', 'battery-tester']]
  ]);

  const normalize = (value = '') => String(value)
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const rootsWithin = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-mbscan-root]')) roots.push(scope);
    scope.querySelectorAll?.('[data-mbscan-root]').forEach((root) => roots.push(root));
    return roots;
  };

  const initMultiBrandCollection = (root) => {
    if (!(root instanceof Element) || instances.has(root)) return;

    const grid = root.querySelector('[data-product-grid]');
    if (!grid) return;

    const cards = Array.from(root.querySelectorAll('[data-product-card]'));
    const tierSections = Array.from(root.querySelectorAll('[data-tier-section]'));
    const tierGrids = new Map(
      Array.from(root.querySelectorAll('[data-tier-grid]'))
        .map((node) => [node.dataset.tierGrid || '', node])
    );
    const filterGroups = Array.from(root.querySelectorAll('[data-filter-group]'));
    const checkboxes = Array.from(root.querySelectorAll('[data-filter-group] input[type="checkbox"]'));
    const searchInput = root.querySelector('[data-product-search]');
    const minInput = root.querySelector('[data-price-min]');
    const maxInput = root.querySelector('[data-price-max]');
    const sortSelect = root.querySelector('[data-sort-select]');
    const resultCount = root.querySelector('[data-result-count]');
    const noResults = root.querySelector('[data-no-results]');
    const activeFilters = root.querySelector('[data-active-filters]');
    const activeCount = root.querySelector('[data-active-count]');
    const filterPanel = root.querySelector('[data-filter-panel]');
    const filterBackdrop = root.querySelector('[data-filter-backdrop]');
    const filterTrigger = root.querySelector('[data-filter-open]');
    const mobileFilterSlot = root.querySelector('[data-mobile-filter-sticky]');
    const mobileFilterDock = root.querySelector('[data-mobile-filter-dock]');
    const navigationHeader = document.querySelector('.icarsoft-nav__header');
    const filterClosers = Array.from(root.querySelectorAll('[data-filter-close]'));
    const quickButtons = Array.from(root.querySelectorAll('[data-quick-filter]'));
    const compareTray = root.querySelector('[data-compare-tray]');
    const compareCount = root.querySelector('[data-compare-count]');
    const compareThumbs = root.querySelector('[data-compare-thumbs]');
    const compareOpen = root.querySelector('[data-compare-open]');
    const compareModal = root.querySelector('[data-compare-modal]');
    const compareTable = root.querySelector('[data-compare-table]');
    const mediaQuery = window.matchMedia('(max-width: 989px)');
    const removers = [];

    let selectedCompare = [];
    let drawerReturnFocus = null;
    let compareReturnFocus = null;
    let restoringUrl = false;
    let mobileFilterTimer = 0;
    let mobileFilterResizeObserver = null;

    root.dataset.mbscanInitialized = 'true';
    root.dataset.mbscanVersion = VERSION;

    cards.forEach((card, index) => {
      if (!card.dataset.index) card.dataset.index = String(index);
      if (!card.dataset.search) {
        card.dataset.search = normalize([
          card.dataset.title,
          card.dataset.coverage,
          card.dataset.features,
          card.dataset.filterTokens,
          card.textContent
        ].filter(Boolean).join(' '));
      }
    });

    const on = (target, eventName, handler, options) => {
      if (!target?.addEventListener) return;
      target.addEventListener(eventName, handler, options);
      removers.push(() => target.removeEventListener(eventName, handler, options));
    };

    const tokenSetFor = (card) => new Set(
      normalize(card.dataset.filterTokens || '')
        .split(/[\s,|]+/)
        .filter(Boolean)
    );

    const selectedByGroup = () => {
      const selected = new Map();
      filterGroups.forEach((group) => {
        const values = Array.from(group.querySelectorAll('input[type="checkbox"]:checked'))
          .map((input) => normalize(input.value))
          .filter(Boolean);
        if (values.length) selected.set(group.dataset.filterGroup || '', values);
      });
      return selected;
    };

    const matchesGroups = (card, selections) => {
      const tokens = tokenSetFor(card);

      // Values selected inside one group are OR; every selected group must match (AND).
      for (const values of selections.values()) {
        if (!values.some((value) => (filterAliases.get(value) || [value]).some((token) => tokens.has(token)))) {
          return false;
        }
      }
      return true;
    };

    const cardPriceInCents = (card) => {
      const raw = Number.parseFloat(card.dataset.price || '0');
      if (!Number.isFinite(raw)) return 0;
      const unit = card.dataset.priceUnit || root.dataset.priceUnit || 'cents';
      return unit === 'major' || unit === 'dollars' ? Math.round(raw * 100) : raw;
    };

    const currencySymbol = () => (
      root.dataset.currencySymbol
      || minInput?.closest('.mbscan__price-input')?.querySelector('b')?.textContent.trim()
      || maxInput?.closest('.mbscan__price-input')?.querySelector('b')?.textContent.trim()
      || '$'
    );

    const setTierCount = (section, visible) => {
      const node = section.querySelector('[data-tier-count]')
        || section.querySelector('.mbscan__tier-heading > span:last-child');
      if (!node) return;

      if (!node.dataset.mbscanCountTemplate) {
        node.dataset.mbscanCountTemplate = node.getAttribute('data-tier-count-template')
          || node.textContent.trim()
          || '{count} tools';
      }

      if (node.hasAttribute('data-tier-count-value')) {
        node.textContent = String(visible);
        return;
      }

      let label = node.dataset.mbscanCountTemplate;
      if (/\{\{?\s*count\s*\}?\}/i.test(label)) {
        label = label.replace(/\{\{?\s*count\s*\}?\}/gi, String(visible));
      } else if (/\d[\d,]*/.test(label)) {
        label = label.replace(/\d[\d,]*/, String(visible));
      } else {
        label = `${visible} ${label}`;
      }

      label = visible === 1
        ? label.replace(/\btools\b/i, 'tool')
        : label.replace(/\btool\b(?!s)/i, 'tools');
      node.textContent = label;
    };

    const updateUrl = () => {
      if (restoringUrl || !window.history?.replaceState) return;

      const params = new URLSearchParams(window.location.search);
      const selected = checkboxes.filter((input) => input.checked).map((input) => input.value);
      const query = searchInput?.value.trim() || '';
      const min = minInput?.value.trim() || '';
      const max = maxInput?.value.trim() || '';
      const sort = sortSelect?.value || 'featured';

      if (selected.length) params.set('filters', selected.join(','));
      else params.delete('filters');

      if (query) params.set('q', query);
      else params.delete('q');

      if (min) params.set('min', min);
      else params.delete('min');

      if (max) params.set('max', max);
      else params.delete('max');

      if (sort !== 'featured') params.set('sort', sort);
      else params.delete('sort');

      const queryString = params.toString();
      const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    };

    const makeChip = (label, clear) => {
      const chip = document.createElement('span');
      const button = document.createElement('button');
      chip.className = 'mbscan__active-chip';
      chip.append(document.createTextNode(`${label} `));
      button.type = 'button';
      button.textContent = '×';
      button.setAttribute('aria-label', `Remove ${label}`);
      button.addEventListener('click', () => {
        clear();
        applyFilters();
      });
      chip.append(button);
      return chip;
    };

    const renderActiveChips = () => {
      if (!activeFilters) return;
      activeFilters.replaceChildren();
      let count = 0;

      checkboxes.filter((input) => input.checked).forEach((input) => {
        const label = input.closest('label')?.querySelector('span')?.textContent.trim() || input.value;
        activeFilters.append(makeChip(label, () => { input.checked = false; }));
        count += 1;
      });

      const query = searchInput?.value.trim();
      if (query) {
        activeFilters.append(makeChip(`Search: ${query}`, () => { searchInput.value = ''; }));
        count += 1;
      }

      const symbol = currencySymbol();
      if (minInput?.value.trim()) {
        activeFilters.append(makeChip(`From ${symbol}${minInput.value.trim()}`, () => { minInput.value = ''; }));
        count += 1;
      }
      if (maxInput?.value.trim()) {
        activeFilters.append(makeChip(`Up to ${symbol}${maxInput.value.trim()}`, () => { maxInput.value = ''; }));
        count += 1;
      }

      if (activeCount) {
        activeCount.textContent = String(count);
        activeCount.hidden = count === 0;
      }
    };

    const applyFilters = () => {
      const selections = selectedByGroup();
      const query = normalize(searchInput?.value || '');
      const minValue = Number.parseFloat(minInput?.value || '');
      const maxValue = Number.parseFloat(maxInput?.value || '');
      const min = Number.isFinite(minValue) ? minValue * 100 : null;
      const max = Number.isFinite(maxValue) ? maxValue * 100 : null;
      let totalVisible = 0;

      cards.forEach((card) => {
        const price = cardPriceInCents(card);
        const searchable = normalize(card.dataset.search || card.textContent || '');
        const show = matchesGroups(card, selections)
          && (!query || searchable.includes(query))
          && (min === null || price >= min)
          && (max === null || price <= max);

        card.hidden = !show;
        if (show) totalVisible += 1;
      });

      tierSections.forEach((section) => {
        const visibleInTier = Array.from(section.querySelectorAll('[data-product-card]'))
          .filter((card) => !card.hidden).length;
        setTierCount(section, visibleInTier);
        section.hidden = visibleInTier === 0;
      });

      if (resultCount) resultCount.textContent = String(totalVisible);
      if (noResults) noResults.hidden = totalVisible !== 0;
      grid.hidden = totalVisible === 0;

      quickButtons.forEach((button) => {
        const value = normalize(button.dataset.quickFilter || '');
        const pressed = checkboxes.some((input) => normalize(input.value) === value && input.checked);
        button.setAttribute('aria-pressed', String(pressed));
      });

      renderActiveChips();
      updateUrl();
      scheduleMobileFilterDock();
    };

    const sortCards = () => {
      const mode = sortSelect?.value || 'featured';
      const sorted = [...cards].sort((a, b) => {
        if (mode === 'price-asc') return cardPriceInCents(a) - cardPriceInCents(b);
        if (mode === 'price-desc') return cardPriceInCents(b) - cardPriceInCents(a);
        if (mode === 'title-asc') return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        if (mode === 'title-desc') return (b.dataset.title || '').localeCompare(a.dataset.title || '');
        return Number(a.dataset.index || 0) - Number(b.dataset.index || 0);
      });

      sorted.forEach((card) => {
        const originalTier = card.closest('[data-tier-grid]')?.dataset.tierGrid;
        const destination = tierGrids.get(card.dataset.tier || '')
          || tierGrids.get(originalTier || '')
          || tierGrids.get('semi-pro')
          || grid;
        destination?.append(card);
      });
      applyFilters();
    };

    const clearFilters = () => {
      checkboxes.forEach((input) => { input.checked = false; });
      if (searchInput) searchInput.value = '';
      if (minInput) minInput.value = '';
      if (maxInput) maxInput.value = '';
      if (sortSelect) sortSelect.value = 'featured';
      sortCards();
    };

    const restoreState = () => {
      restoringUrl = true;
      const params = new URLSearchParams(window.location.search);
      const values = new Set((params.get('filters') || '')
        .split(',')
        .map((value) => normalize(value.includes(':') ? value.split(':').slice(1).join(':') : value))
        .filter(Boolean));

      checkboxes.forEach((input) => { input.checked = values.has(normalize(input.value)); });
      if (searchInput) searchInput.value = params.get('q') || '';
      if (minInput) minInput.value = params.get('min') || '';
      if (maxInput) maxInput.value = params.get('max') || '';

      if (sortSelect) {
        const requestedSort = params.get('sort') || 'featured';
        sortSelect.value = Array.from(sortSelect.options).some((option) => option.value === requestedSort)
          ? requestedSort
          : 'featured';
      }
      restoringUrl = false;
    };

    const setDrawerAccessibility = (isOpen) => {
      if (!filterPanel) return;

      if (!mediaQuery.matches) {
        filterPanel.removeAttribute('aria-hidden');
        filterPanel.removeAttribute('inert');
        if ('inert' in filterPanel) filterPanel.inert = false;
        return;
      }

      filterPanel.setAttribute('aria-hidden', String(!isOpen));
      if ('inert' in filterPanel) filterPanel.inert = !isOpen;
      else if (isOpen) filterPanel.removeAttribute('inert');
      else filterPanel.setAttribute('inert', '');
    };

    const closeDrawer = ({ returnFocus = true } = {}) => {
      if (!filterPanel || !filterBackdrop) return;
      filterPanel.classList.remove('is-open');
      filterBackdrop.hidden = true;
      filterTrigger?.setAttribute('aria-expanded', 'false');
      setDrawerAccessibility(false);
      if (!document.querySelector('[data-mbscan-root] [data-filter-panel].is-open')) {
        document.body.classList.remove('mbscan-filter-open');
      }
      if (returnFocus && drawerReturnFocus?.focus) drawerReturnFocus.focus({ preventScroll: true });
      drawerReturnFocus = null;
    };

    const openDrawer = () => {
      if (!mediaQuery.matches || !filterPanel || !filterBackdrop) return;
      drawerReturnFocus = document.activeElement;
      setDrawerAccessibility(true);
      filterPanel.classList.add('is-open');
      filterBackdrop.hidden = false;
      filterTrigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mbscan-filter-open');
      filterPanel.querySelector('input:not([disabled]), button:not([disabled]), summary')?.focus({ preventScroll: true });
    };

    const syncDrawerViewport = () => {
      if (!mediaQuery.matches) closeDrawer({ returnFocus: false });
      else setDrawerAccessibility(filterPanel?.classList.contains('is-open'));
      scheduleMobileFilterDock();
    };

    const updateMobileFilterDock = () => {
      if (!mobileFilterSlot || !mobileFilterDock) return;

      if (!mediaQuery.matches) {
        mobileFilterDock.classList.remove('is-fixed');
        mobileFilterDock.style.removeProperty('--mbscan-mobile-filter-top');
        return;
      }

      const fallbackTop = window.innerWidth <= 768 ? 145 : 76;
      const headerBottom = navigationHeader?.getBoundingClientRect().bottom;
      const top = window.innerWidth <= 768
        ? fallbackTop
        : Math.max(0, Math.round(Number.isFinite(headerBottom) ? headerBottom : fallbackTop));
      const slotTop = mobileFilterSlot.getBoundingClientRect().top;
      const shouldFix = slotTop <= top;

      mobileFilterDock.style.setProperty('--mbscan-mobile-filter-top', `${top}px`);
      mobileFilterDock.classList.toggle('is-fixed', shouldFix);
    };

    const scheduleMobileFilterDock = () => {
      updateMobileFilterDock();
      if (mobileFilterTimer) window.clearTimeout(mobileFilterTimer);
      mobileFilterTimer = window.setTimeout(() => {
        mobileFilterTimer = 0;
        updateMobileFilterDock();
      }, 280);
    };

    const countQuickCategories = () => {
      root.querySelectorAll('[data-count-for]').forEach((node) => {
        const token = normalize(node.dataset.countFor || '');
        node.textContent = String(cards.filter((card) => tokenSetFor(card).has(token)).length);
      });
    };

    const compareData = (card) => ({
      title: card.dataset.title || '',
      url: card.dataset.url || '#',
      image: card.dataset.image || card.querySelector('.mbscan__product-media img')?.currentSrc || '',
      price: card.querySelector('[data-product-price], .mbscan__product-price span')?.textContent.trim() || '',
      level: card.dataset.level || '',
      coverage: card.dataset.coverage || '',
      user: card.dataset.user || '',
      controls: card.dataset.controls || '',
      connection: card.dataset.connection || '',
      updates: card.dataset.updates || '',
      features: card.dataset.features || ''
    });

    const updateCompareTray = () => {
      if (!compareTray || !compareThumbs || !compareOpen) return;
      compareTray.hidden = selectedCompare.length === 0;
      if (compareCount) compareCount.textContent = String(selectedCompare.length);
      compareOpen.disabled = selectedCompare.length < 2;
      compareOpen.setAttribute('aria-disabled', String(selectedCompare.length < 2));
      compareThumbs.innerHTML = selectedCompare.map((card) => {
        const item = compareData(card);
        return `<span class="mbscan__compare-thumb" title="${escapeHtml(item.title)}">${item.image ? `<img src="${escapeHtml(item.image)}" alt="">` : ''}</span>`;
      }).join('');
    };

    const renderCompareTable = () => {
      if (!compareTable) return;
      const items = selectedCompare.map(compareData);
      const header = items.map((item) => `
        <th scope="col">
          <a class="mbscan__compare-product" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="">` : ''}
            <strong>${escapeHtml(item.title)}</strong>
            <span>${escapeHtml(item.price)}</span>
          </a>
        </th>`).join('');
      const rowDefinitions = [
        ['Diagnostic level', 'level'],
        ['Coverage', 'coverage'],
        ['Best for', 'user'],
        ['Controls', 'controls'],
        ['Vehicle connection', 'connection'],
        ['Updates', 'updates'],
        ['Highlights', 'features']
      ];
      const rows = rowDefinitions.map(([label, key]) => `
        <tr>
          <th scope="row">${escapeHtml(label)}</th>
          ${items.map((item) => `<td>${escapeHtml(item[key])}</td>`).join('')}
        </tr>`).join('');

      compareTable.innerHTML = `<table class="mbscan__compare-table"><thead><tr><th></th>${header}</tr></thead><tbody>${rows}</tbody></table>`;
    };

    const closeCompareDialog = ({ returnFocus = true } = {}) => {
      if (!compareModal) return;
      if (typeof compareModal.close === 'function' && compareModal.open) compareModal.close();
      else compareModal.removeAttribute('open');
      if (returnFocus && compareReturnFocus?.focus) compareReturnFocus.focus();
      compareReturnFocus = null;
    };

    const openCompareDialog = () => {
      if (selectedCompare.length < 2 || !compareModal) return;
      compareReturnFocus = document.activeElement;
      renderCompareTable();
      if (typeof compareModal.showModal === 'function') {
        if (!compareModal.open) compareModal.showModal();
      } else {
        compareModal.setAttribute('open', '');
      }
    };

    const clearCompare = () => {
      selectedCompare.forEach((card) => {
        const toggle = card.querySelector('[data-compare-toggle]');
        if (toggle) toggle.checked = false;
      });
      selectedCompare = [];
      closeCompareDialog({ returnFocus: false });
      updateCompareTray();
    };

    checkboxes.forEach((input) => on(input, 'change', applyFilters));
    on(searchInput, 'input', applyFilters);
    on(minInput, 'input', applyFilters);
    on(maxInput, 'input', applyFilters);
    on(sortSelect, 'change', sortCards);
    root.querySelectorAll('[data-clear-filters]').forEach((button) => on(button, 'click', clearFilters));

    on(filterTrigger, 'click', openDrawer);
    filterClosers.forEach((button) => on(button, 'click', () => closeDrawer()));
    on(filterBackdrop, 'click', () => closeDrawer());
    on(window, 'scroll', scheduleMobileFilterDock, { passive: true });
    on(window, 'resize', scheduleMobileFilterDock);
    on(window.visualViewport, 'resize', scheduleMobileFilterDock);
    on(navigationHeader, 'transitionend', scheduleMobileFilterDock);

    if (navigationHeader && 'ResizeObserver' in window) {
      mobileFilterResizeObserver = new ResizeObserver(scheduleMobileFilterDock);
      mobileFilterResizeObserver.observe(navigationHeader);
    }

    quickButtons.forEach((button) => on(button, 'click', () => {
      const value = normalize(button.dataset.quickFilter || '');
      const target = checkboxes.find((input) => normalize(input.value) === value);
      if (!target) return;

      if (root.dataset.quickFilterMode === 'preserve-group') {
        const wasChecked = target.checked;
        const group = target.closest('[data-filter-group]');
        group?.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
        target.checked = !wasChecked;
      } else {
        checkboxes.forEach((input) => { input.checked = false; });
        target.checked = true;
      }
      applyFilters();
      root.querySelector('.mbscan__catalog')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));

    cards.forEach((card) => {
      const toggle = card.querySelector('[data-compare-toggle]');
      on(toggle, 'change', () => {
        if (toggle.checked) {
          if (selectedCompare.length >= 3) {
            toggle.checked = false;
            window.alert(root.dataset.compareLimitMessage || 'Choose up to three scan tools to compare.');
            return;
          }
          if (!selectedCompare.includes(card)) selectedCompare.push(card);
        } else {
          selectedCompare = selectedCompare.filter((selected) => selected !== card);
        }
        updateCompareTray();
      });
    });

    on(root.querySelector('[data-compare-clear]'), 'click', clearCompare);
    on(compareOpen, 'click', openCompareDialog);
    on(root.querySelector('[data-compare-close]'), 'click', () => closeCompareDialog());
    on(compareModal, 'click', (event) => {
      if (event.target === compareModal) closeCompareDialog();
    });
    on(compareModal, 'close', () => {
      if (compareReturnFocus?.focus) compareReturnFocus.focus();
      compareReturnFocus = null;
    });

    on(document, 'keydown', (event) => {
      if (event.key === 'Tab' && filterPanel?.classList.contains('is-open')) {
        const focusable = Array.from(filterPanel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        )).filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      if (event.key !== 'Escape') return;
      if (filterPanel?.classList.contains('is-open')) {
        event.preventDefault();
        closeDrawer();
      } else if (compareModal?.hasAttribute('open') && typeof compareModal.close !== 'function') {
        event.preventDefault();
        closeCompareDialog();
      }
    });

    if (typeof mediaQuery.addEventListener === 'function') on(mediaQuery, 'change', syncDrawerViewport);
    else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(syncDrawerViewport);
      removers.push(() => mediaQuery.removeListener(syncDrawerViewport));
    }

    on(window, 'popstate', () => {
      restoreState();
      sortCards();
    });

    restoreState();
    countQuickCategories();
    sortCards();
    syncDrawerViewport();
    updateCompareTray();

    const destroy = () => {
      closeDrawer({ returnFocus: false });
      closeCompareDialog({ returnFocus: false });
      if (mobileFilterTimer) window.clearTimeout(mobileFilterTimer);
      mobileFilterResizeObserver?.disconnect();
      mobileFilterDock?.classList.remove('is-fixed');
      mobileFilterDock?.style.removeProperty('--mbscan-mobile-filter-top');
      removers.splice(0).forEach((remove) => remove());
      root.removeAttribute('data-mbscan-initialized');
      root.removeAttribute('data-mbscan-version');
      instances.delete(root);
    };

    instances.set(root, { destroy, openDrawer });
  };

  const initAll = (scope = document) => rootsWithin(scope).forEach(initMultiBrandCollection);
  const destroyAll = (scope) => rootsWithin(scope).forEach((root) => instances.get(root)?.destroy());

  const handleDelegatedFilterOpen = (event) => {
    const trigger = event.target.closest?.('[data-mbscan-root] [data-filter-open]');
    if (!trigger || trigger.getAttribute('aria-expanded') === 'true') return;

    const root = trigger.closest('[data-mbscan-root]');
    if (!root) return;
    initMultiBrandCollection(root);
    instances.get(root)?.openDrawer();
  };

  window[GLOBAL_KEY] = { version: VERSION, initAll, destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  document.addEventListener('click', handleDelegatedFilterOpen);
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-mbscan-root]');
    const blockElement = event.target.closest?.('[data-shopify-editor-block]');
    if (!root || !blockElement) return;

    const card = blockElement.closest('[data-product-card]');
    if (card?.hidden) root.querySelector('[data-clear-filters]')?.click();

    if (blockElement.matches('details')) blockElement.open = true;
    requestAnimationFrame(() => {
      blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
})();
